<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ProposalModal from '../components/ProposalModal.svelte';
  import SignatureInfoModal from '../components/SignatureInfoModal.svelte';
  import UploadSignedPdfModal from '../components/UploadSignedPdfModal.svelte';
  import ManualConfirmModal from '../components/ManualConfirmModal.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';
  import SignatureAttestationModal from '../components/SignatureAttestationModal.svelte';
  import SignatureStateBadge from '../components/SignatureStateBadge.svelte';
  import {
    signatureStateOf,
    requiresAttestationFor,
    isCountersignable
  } from '../components/signatureState.js';
  import { proposalsApi, customersApi, customerPortalApi } from '../stores/api.js';
  import { canCreateProposal, canSendProposal, canConfirmProposal, canCancelProposal, canReviseProposal, canSignDocuments, isAdmin, isCustomer } from '../stores/roles.js';
  import { authStore } from '../stores/auth.js';
  import { _ } from '../lib/i18n.js';
  import { FileText, Plus, Search, Eye, Edit, Send, Download, History, Loader, AlertCircle, Check, Ban, ThumbsUp, FileSignature, Upload, Trash2, FileCheck, GripVertical, PenTool, GitBranch, RefreshCw } from 'lucide-svelte';

  let proposals = [];
  let customers = [];
  let loading = true;
  let error = null;
  let statusFilter = '';
  let searchQuery = '';
  // Set only by "open this revision in the list", and cleared as soon as the
  // user filters or searches for something else.
  let rootIdFilter = null;
  let pagination = { page: 1, limit: 20, total: 0, pages: 0 };

  // Modal states
  let showCreateModal = false;
  let showCancelModal = false;
  let showDeleteModal = false;
  let showHistoryModal = false;
  let showSignatureModal = false;
  let showUploadModal = false;
  let showManualConfirmModal = false;
  let showReviseModal = false;
  let showRevisionsModal = false;
  let showCountersignModal = false;
  let countersigning = false;
  let uploadType = 'customer'; // 'customer' or 'final'

  // Attestation dialog for a document the system could not verify. Shared by
  // the counter-sign and confirm actions, which are the two acts the backend
  // refuses without it.
  let showAttestationModal = false;
  let attestationAction = 'confirm'; // 'countersign' or 'confirm'
  let attestationInfo = null;
  let attestationState = null;
  let attestationServerMessage = null;
  let attestationError = null;
  let attestationLoading = false;

  let selectedProposal = null;
  let cancelNotes = '';
  let reviseReason = '';
  let revisions = [];
  let revisionsLoading = false;
  let history = [];
  let actionLoading = false;
  let actionMessage = null;

  // Edit mode
  let editingProposal = null;

  // Column resize state.
  //
  // The actions column is sized for its widest real row, because the table is
  // `table-layout: fixed`: a cell narrower than its content does not grow, it
  // spills, and the overflow is clipped by the horizontal scroll area. An admin
  // looking at a `customer_approved` proposal now sees nine controls — download,
  // history, upload-final, counter-sign, confirm, signature-info,
  // manual-confirm, cancel, delete — at 23px each (a 15px icon in 4px padding)
  // with 4px gaps: 239px, plus the cell's own 8px sides. It was 160px, which fit
  // four and a half of them; the counter-sign button had never rendered, so the
  // row had never been laid out at its real width.
  const ACTIONS_COL_WIDTH = 256;
  let columnWidths = {
    id: 80,
    title: 200,
    customer: 160,
    status: 120,
    basePrice: 120,
    proposedPrice: 120,
    margin: 130,
    finalPrice: 120,
    createdAt: 110,
  };

  let resizing = null; // { key, startX, startWidth }

  function onResizeStart(e, key) {
    e.preventDefault();
    const startX = e.clientX || e.touches?.[0]?.clientX;
    resizing = { key, startX, startWidth: columnWidths[key] };
    console.log(`[Proposals] Column resize started: ${key}, width: ${columnWidths[key]}`);

    function onMove(ev) {
      const clientX = ev.clientX || ev.touches?.[0]?.clientX;
      const diff = clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + diff);
      columnWidths[key] = newWidth;
      columnWidths = columnWidths; // trigger reactivity
    }

    function onEnd() {
      console.log(`[Proposals] Column resize ended: ${resizing.key}, new width: ${columnWidths[resizing.key]}`);
      resizing = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
  }

  const statusColors = {
    draft: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    sent: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
    seen: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
    customer_approved: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300',
    confirmed: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
    cancelled: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
    // Withdrawn, not refused: amber rather than the red of a cancellation, and
    // muted, because it is evidence of what was sent rather than live work.
    superseded: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
  };

  /**
   * Statuses each destructive action is still available for, mirroring the
   * backend allowlists. Offering an action the server will refuse is not a
   * cosmetic problem: it teaches the user that the button is unreliable.
   */
  const MANUALLY_CONFIRMABLE_STATUSES = ['sent', 'seen', 'customer_approved'];
  const CANCELLABLE_STATUSES = ['draft', 'sent', 'seen', 'customer_approved'];

  /**
   * Whether this row is a revision the signed-in sales user created themselves,
   * which is the one thing a non-admin may cancel. The backend checks the same
   * three facts; this only keeps the button from appearing where it would 403.
   * @param {Object} proposal - Proposal row
   * @returns {boolean}
   */
  function isOwnDraftRevision(proposal) {
    return proposal.status === 'draft'
      && proposal.supersedes_id != null
      && proposal.created_by === $authStore.user?.userId;
  }

  /**
   * The document name of a proposal: its number, and its revision index once
   * there is more than one revision of the offer.
   *
   * A second implementation of the rule in `backend/src/utils/proposalDocumentName.js`,
   * which names every server-side filename and the e-mail attachment. The two
   * builds share no code, so they are kept in step by hand: change one and change
   * the other, or a downloaded file stops matching the document the customer was
   * sent under that name.
   *
   * @param {Object} proposal - Proposal or revision row
   * @returns {string}
   */
  function proposalDocumentName(proposal) {
    const base = proposal.proposal_number || proposal.id;
    return proposal.revision > 1 ? `${base}_rev${proposal.revision}` : `${base}`;
  }

  /**
   * Whether the delete action applies. A transmitted proposal and a revision are
   * both undeletable: the first is evidence, the second is abandoned by
   * cancelling it so the chain stays legible.
   * @param {Object} proposal - Proposal row
   * @returns {boolean}
   */
  function isDeletable(proposal) {
    return proposal.status === 'draft' && !proposal.sent_at && proposal.supersedes_id == null;
  }

  onMount(loadProposals);

  async function loadProposals() {
    loading = true;
    error = null;
    try {
      let response;

      if ($isCustomer) {
        // Customer users use the customer portal API
        response = await customerPortalApi.getProposals();
        proposals = response.proposals || [];
        console.log('[Proposals] Loaded', proposals.length, 'proposals for customer');
      } else {
        // Internal users use the internal API
        const params = { page: pagination.page, limit: pagination.limit };
        if (statusFilter) params.status = statusFilter;
        if (searchQuery) params.search = searchQuery;
        if (rootIdFilter != null) params.rootId = rootIdFilter;

        response = await proposalsApi.getAll(params);
        proposals = response.proposals || [];
        pagination = response.pagination || pagination;
        console.log('[Proposals] Loaded', proposals.length, 'proposals');
      }
    } catch (err) {
      console.error('[Proposals] Error loading:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  /**
   * The state of one of a proposal's two documents, from the row itself.
   *
   * Two shapes reach this page. The internal reads publish
   * `customerSignatureState` / `finalSignatureState` — a state string, or
   * `null` when no document of that kind is stored. The customer portal's list
   * still carries the (projected) records instead, so those are resolved the
   * same way the panel resolves a record.
   *
   * @param {Object} proposal Proposal row
   * @param {'customer'|'final'} which Which document
   * @returns {'bound'|'unbound'|'manual'|null} State, or null when none is stored
   */
  function proposalSignatureState(proposal, which) {
    const published = which === 'customer' ? proposal.customerSignatureState : proposal.finalSignatureState;
    if (published !== undefined) return published;
    return signatureStateOf(which === 'customer' ? proposal.customer_signature_info : proposal.final_signature_info);
  }

  async function loadCustomers() {
    try {
      const response = await customersApi.getAll({ limit: 1000 });
      customers = response.customers || [];
    } catch (err) {
      console.error('[Proposals] Error loading customers:', err);
    }
  }

  function openCreateModal() {
    loadCustomers();
    editingProposal = null;
    showCreateModal = true;
  }

  async function openEditModal(proposal) {
    if (proposal.status !== 'draft') return;
    await loadCustomers();
    const full = await proposalsApi.getById(proposal.id);
    console.log('[Proposals] Loaded full proposal for editing:', full);
    editingProposal = full;
    showCreateModal = true;
  }

  async function handleCreateOrUpdate(event) {
    const data = event.detail;
    actionLoading = true;
    try {
      if (editingProposal) {
        await proposalsApi.update(editingProposal.id, data);
        actionMessage = { type: 'success', text: $_('proposals.messages.updated') };
      } else {
        await proposalsApi.create(data);
        actionMessage = { type: 'success', text: $_('proposals.messages.created') };
      }
      showCreateModal = false;
      editingProposal = null;
      await loadProposals();
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
    } finally {
      actionLoading = false;
    }
  }

  async function handleSend(proposal) {
    actionLoading = true;
    try {
      await proposalsApi.send(proposal.id);
      actionMessage = { type: 'success', text: $_('proposals.messages.sent', { values: { email: proposal.customer_email } }) };
      await loadProposals();
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
    } finally {
      actionLoading = false;
    }
  }

  async function handleCustomerApprove(proposal) {
    actionLoading = true;
    try {
      // Use customer portal API if user is a customer, otherwise use internal API
      if ($isCustomer) {
        await customerPortalApi.approveProposal(proposal.id);
      } else {
        await proposalsApi.customerApprove(proposal.id);
      }
      actionMessage = { type: 'success', text: $_('proposals.messages.customerApproved') };
      await loadProposals();
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
    } finally {
      actionLoading = false;
    }
  }

  /**
   * Start confirming a proposal.
   *
   * Confirming is the act that turns a document into a contract. When the final
   * document is `unbound` — the chain could not be verified, or the signed
   * payload does not match the PDF we sent — the backend refuses until a named
   * internal user says they checked it, so the dialog is opened up front with
   * the reason rather than after a refusal the operator cannot act on.
   *
   * @param {Object} proposal Proposal row
   */
  function openConfirm(proposal) {
    selectedProposal = proposal;
    const finalState = proposal.finalSignatureState;
    // No final document at all is not an unverified one: the server answers
    // that with "carica prima il PDF con doppia firma", which is the useful
    // message. Asking for an attestation here would answer a question the
    // operator has not reached yet.
    //
    // Neither is a document that already carries an attestation — one the user
    // made when counter-signing, or the one the system recorded for its own
    // qualified signature. The server does not ask for those again, and asking
    // here would make the operator vouch for our own certificate.
    if (finalState && requiresAttestationFor(finalState) && !proposal.finalSignatureAttested) {
      openAttestation('confirm', finalState, null);
      return;
    }
    runConfirm(proposal, null);
  }

  /**
   * @param {Object} proposal Proposal row
   * @param {{attestUnverifiedSignature: boolean, attestationNotes: string|null}|null} attestation
   */
  async function runConfirm(proposal, attestation) {
    actionLoading = true;
    attestationLoading = !!attestation;
    attestationError = null;
    try {
      await proposalsApi.confirm(proposal.id, null, attestation);
      showAttestationModal = false;
      actionMessage = { type: 'success', text: $_('proposals.messages.confirmed') };
      await loadProposals();
    } catch (err) {
      console.error('[Proposals] Confirmation failed:', err);
      // The refusal is recognisable, not just readable: reopen as a request for
      // the attestation, carrying the state and the reason the server gave.
      if (err.requiresAttestation && !attestation) {
        openAttestation('confirm', err.signatureState, err.message);
      } else if (showAttestationModal) {
        attestationError = err.message;
      } else {
        actionMessage = { type: 'error', text: err.message };
      }
    } finally {
      actionLoading = false;
      attestationLoading = false;
    }
  }

  /**
   * Open the dialog covering one act on one document that cannot just proceed.
   *
   * The row publishes the state but not the diagnostics, so the record is
   * fetched once, here, when the dialog opens — not per row. That is the whole
   * difference between "questo documento non è verificato" and naming the check
   * that actually failed, which is what the operator needs to decide.
   *
   * @param {'countersign'|'confirm'} action Which act the dialog covers
   * @param {string|null} state State from the row or from a refusal
   * @param {string|null} serverMessage The server's own refusal sentence, if any
   */
  function openAttestation(action, state, serverMessage) {
    attestationAction = action;
    attestationInfo = null;
    attestationState = state || null;
    attestationServerMessage = serverMessage || null;
    attestationError = null;
    showCountersignModal = false;
    showAttestationModal = true;
    console.log(`[Proposals] Signature dialog for ${action} on proposal ${selectedProposal?.id} (state: ${attestationState || 'unknown'})`);

    if (selectedProposal && !$isCustomer) {
      hydrateAttestationInfo(selectedProposal.id, action);
    }
  }

  /**
   * Load the signature record behind the dialog, best effort.
   *
   * Its only job is to add the reasons; the dialog is already usable without
   * it, so a failure is logged and nothing else.
   *
   * @param {number} proposalId Proposal the dialog is open on
   * @param {'countersign'|'confirm'} action Which document the dialog covers
   */
  async function hydrateAttestationInfo(proposalId, action) {
    try {
      const records = await proposalsApi.getSignatureInfo(proposalId);
      // Only adopt it if the operator is still looking at the same dialog.
      if (showAttestationModal && attestationAction === action && selectedProposal?.id === proposalId) {
        attestationInfo = (action === 'countersign' ? records?.customer : records?.final) || null;
      }
    } catch (err) {
      console.warn(`[Proposals] Could not read the signature record of proposal ${proposalId}: ${err?.message}`);
    }
  }

  /**
   * The operator ticked the box and submitted: replay the act, this time
   * carrying the attestation.
   *
   * @param {CustomEvent<{attestUnverifiedSignature: boolean, attestationNotes: string|null}>} event
   */
  function handleAttest(event) {
    if (!selectedProposal) return;
    if (attestationAction === 'countersign') {
      handleCountersign(event.detail);
    } else {
      runConfirm(selectedProposal, event.detail);
    }
  }

  function closeAttestation() {
    showAttestationModal = false;
    attestationInfo = null;
    attestationState = null;
    attestationServerMessage = null;
    attestationError = null;
    selectedProposal = null;
  }

  function openCancelModal(proposal) {
    selectedProposal = proposal;
    cancelNotes = '';
    showCancelModal = true;
  }

  async function handleCancel() {
    if (!cancelNotes.trim()) return;
    actionLoading = true;
    try {
      await proposalsApi.cancel(selectedProposal.id, cancelNotes);
      showCancelModal = false;
      actionMessage = { type: 'success', text: $_('proposals.messages.cancelled') };
      await loadProposals();
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
    } finally {
      actionLoading = false;
    }
  }

  function openDeleteModal(proposal) {
    selectedProposal = proposal;
    showDeleteModal = true;
  }

  function openReviseModal(proposal) {
    selectedProposal = proposal;
    reviseReason = '';
    showReviseModal = true;
  }

  async function handleRevise() {
    if (!reviseReason.trim()) return;
    actionLoading = true;
    try {
      const created = await proposalsApi.revise(selectedProposal.id, reviseReason.trim());
      showReviseModal = false;
      actionMessage = {
        type: 'success',
        text: $_('proposals.messages.revised', { values: { revision: created.revision } })
      };
      await loadProposals();
      // Straight into the new draft: the whole point of revising is to change
      // something, and the revision starts as an exact copy.
      const child = proposals.find(p => p.id === created.id);
      if (child) openEditModal(child);
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
    } finally {
      actionLoading = false;
    }
  }

  /**
   * Bring one revision of the chain into the list, where its own actions are.
   * A withdrawn revision is outside the default list, so the status filter is
   * what opens it — the same route the user guide describes. The chain filter
   * comes with it: with several withdrawn proposals in the system, the status
   * alone buries the row the user asked for among unrelated ones.
   * @param {Object} revision - A row from the revision chain
   */
  async function openRevisionInList(revision) {
    showRevisionsModal = false;
    statusFilter = revision.status;
    rootIdFilter = revision.root_id ?? null;
    pagination.page = 1;
    await loadProposals();
  }

  async function openRevisionsModal(proposal) {
    selectedProposal = proposal;
    revisions = [];
    revisionsLoading = true;
    showRevisionsModal = true;
    try {
      const response = await proposalsApi.getRevisions(proposal.id);
      revisions = response.revisions || [];
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
      showRevisionsModal = false;
    } finally {
      revisionsLoading = false;
    }
  }

  async function handleDelete() {
    actionLoading = true;
    try {
      await proposalsApi.delete(selectedProposal.id);
      showDeleteModal = false;
      actionMessage = { type: 'success', text: $_('proposals.messages.deleted') };
      await loadProposals();
    } catch (err) {
      actionMessage = { type: 'error', text: err.message };
    } finally {
      actionLoading = false;
    }
  }

  async function downloadPdf(proposal) {
    try {
      const url = proposalsApi.getPdfUrl(proposal.id);
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Two revisions of one chain share a proposal number, so without this they
      // land in the same downloads folder under the same name.
      a.download = `proposta_${proposalDocumentName(proposal)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download error:', err);
      alert($_('proposals.messages.pdfDownloadError'));
    }
  }

  function openSignatureModal(proposal) {
    selectedProposal = proposal;
    showSignatureModal = true;
  }

  /**
   * Open the counter-signature flow for a proposal.
   *
   * A `bound` customer document goes through the plain confirmation dialog, as
   * before. Anything else goes through the attestation dialog instead: applying
   * our qualified signature asserts that this is the document we sent, and when
   * the system could not establish that, a named user has to.
   *
   * @param {Object} proposal Proposal row
   */
  function openCountersign(proposal) {
    selectedProposal = proposal;
    const customerState = proposal.customerSignatureState;
    // `manual` is refused outright by the server, with no attestation that could
    // help: the dialog explains that instead of offering a box to tick.
    // `unbound` asks for the attestation. `bound` keeps the plain confirmation.
    if (!isCountersignable(customerState)
      || (requiresAttestationFor(customerState) && !proposal.customerSignatureAttested)) {
      openAttestation('countersign', customerState, null);
      return;
    }
    showCountersignModal = true;
  }

  /**
   * Counter-sign the customer-signed proposal with the company's qualified
   * certificate, producing the final .p7m.
   *
   * The confirmation step is not decoration: the certificate is automatic, so
   * this click *is* the signature — no PIN, no OTP, no undo.
   *
   * @param {{attestUnverifiedSignature: boolean, attestationNotes: string|null}|null} [attestation]
   */
  async function handleCountersign(attestation = null) {
    if (!selectedProposal) return;
    const proposal = selectedProposal;
    countersigning = true;
    attestationLoading = !!attestation;
    attestationError = null;
    try {
      await proposalsApi.countersign(proposal.id, attestation);
      showCountersignModal = false;
      showAttestationModal = false;
      selectedProposal = null;
      actionMessage = { type: 'success', text: $_('signatures.countersignSuccess') };
      await loadProposals();
    } catch (err) {
      console.error('[Proposals] Counter-sign failed:', err);
      if (err.requiresAttestation && !attestation) {
        openAttestation('countersign', err.signatureState, err.message);
      } else if (err.requiresAttestation === false && err.signatureState && !attestation) {
        // A refusal that says an attestation would not help — a manual document
        // — is an explanation, not a prompt. The dialog renders it as one.
        openAttestation('countersign', err.signatureState, err.message);
      } else if (showAttestationModal) {
        attestationError = err.message;
      } else {
        error = err.message;
        showCountersignModal = false;
      }
    } finally {
      countersigning = false;
      attestationLoading = false;
    }
  }

  function openUploadModal(proposal, type) {
    selectedProposal = proposal;
    uploadType = type;
    showUploadModal = true;
  }

  async function handleUploadSuccess(event) {
    const { type, signatureState, signatureInfo } = event.detail;
    console.log(`[Proposals] Upload success for ${type} (state: ${signatureState || 'unknown'}):`, signatureInfo);
    showUploadModal = false;
    actionMessage = { type: 'success', text: $_('proposals.messages.signedPdfUploaded') };
    await loadProposals();
  }

  function openManualConfirmModal(proposal) {
    selectedProposal = proposal;
    showManualConfirmModal = true;
  }

  async function handleManualConfirmSuccess() {
    console.log('[Proposals] Manual confirmation successful');
    showManualConfirmModal = false;
    actionMessage = { type: 'success', text: $_('proposals.messages.manuallyConfirmed') };
    await loadProposals();
  }

  async function openHistoryModal(proposal) {
    selectedProposal = proposal;
    try {
      const response = await proposalsApi.getHistory(proposal.id);
      // Backend returns array directly
      history = Array.isArray(response) ? response : (response.history || []);
      console.log('[Proposals] Loaded history:', history.length, 'entries');
    } catch (err) {
      console.error('[Proposals] Error loading history:', err);
      history = [];
    }
    showHistoryModal = true;
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  }

  function formatDate(date) {
    if (!date) return '-';
    return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date));
  }

  function formatDateTime(date) {
    if (!date) return '-';
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  }

  function handleStatusFilter(e) {
    statusFilter = e.target.value;
    // Asking for a status is asking about the whole archive again, not about the
    // one chain a revision panel scoped the list to.
    rootIdFilter = null;
    pagination.page = 1;
    loadProposals();
  }

  function handleSearch(e) {
    searchQuery = e.target.value;
    rootIdFilter = null;
    pagination.page = 1;
    loadProposals();
  }

  function calculateMargin(proposal) {
    const cost = parseFloat(proposal.base_price) || 0;
    const sellingPrice = parseFloat(proposal.proposed_price) || 0;
    const marginAmount = sellingPrice - cost;
    const marginPercent = cost > 0 ? ((sellingPrice - cost) / cost) * 100 : 0;
    return { amount: marginAmount, percent: marginPercent };
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div class="flex items-center space-x-4">
        <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
          <FileText size={28} class="text-white" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('proposals.title')}</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('proposals.subtitle')}</p>
        </div>
      </div>

      {#if $canCreateProposal}
        <button
          class="btn btn-primary flex items-center space-x-2"
          on:click={openCreateModal}
        >
          <Plus size={20} />
          <span>{$_('proposals.createNew')}</span>
        </button>
      {/if}
    </div>

    <!-- Action Message -->
    {#if actionMessage}
      <div class="p-4 rounded-lg {actionMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {actionMessage.text}
        <button class="float-right text-current hover:opacity-70" on:click={() => actionMessage = null}>&times;</button>
      </div>
    {/if}

    <!-- Filters -->
    <div class="card p-4">
      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div class="relative flex-1">
          <Search size={18} class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            class="input w-full pl-10"
            placeholder={$_('proposals.searchPlaceholder')}
            value={searchQuery}
            on:input={handleSearch}
          />
        </div>
        <select
          class="input max-w-xs"
          value={statusFilter}
          on:change={handleStatusFilter}
        >
          <option value="">{$_('proposals.filters.all')}</option>
          <option value="draft">{$_('proposals.status.draft')}</option>
          <option value="sent">{$_('proposals.status.sent')}</option>
          <option value="seen">{$_('proposals.status.seen')}</option>
          <option value="customer_approved">{$_('proposals.status.customer_approved')}</option>
          <option value="confirmed">{$_('proposals.status.confirmed')}</option>
          <option value="cancelled">{$_('proposals.status.cancelled')}</option>
          <!-- Withdrawn revisions are out of the default list, not out of reach:
               this is how the evidence of what was sent is opened again. -->
          <option value="superseded">{$_('proposals.status.superseded')}</option>
        </select>
        <span class="text-sm text-gray-500 dark:text-gray-400">
          {$_('proposals.totalCount', { values: { count: pagination.total } })}
        </span>
      </div>
    </div>

    <!-- Error -->
    {#if error}
      <div class="card p-6 text-center">
        <AlertCircle size={48} class="mx-auto text-red-500 dark:text-red-400 mb-4" />
        <p class="text-red-600 dark:text-red-400">{error}</p>
        <button class="btn btn-secondary mt-4" on:click={loadProposals}>Riprova</button>
      </div>
    {:else if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('proposals.loading')}</p>
      </div>
    {:else if proposals.length === 0}
      <div class="card p-12 text-center">
        <FileText size={64} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">{$_('proposals.noProposals')}</h3>
        <p class="text-gray-500 dark:text-gray-400 mt-2">{$_('proposals.noProposalsHint')}</p>
      </div>
    {:else}
      <!-- Table -->
      <div class="card overflow-hidden proposals-table-wrapper">
        <div class="overflow-x-auto proposals-scroll-area">
          <table class="table-modern proposals-table" style="table-layout: fixed; min-width: max-content;">
            <colgroup>
              <col style="width: {columnWidths.id}px;" />
              <col style="width: {columnWidths.title}px;" />
              {#if !$isCustomer}
                <col style="width: {columnWidths.customer}px;" />
              {/if}
              <col style="width: {columnWidths.status}px;" />
              {#if !$isCustomer}
                <col style="width: {columnWidths.basePrice}px;" />
                <col style="width: {columnWidths.proposedPrice}px;" />
                <col style="width: {columnWidths.margin}px;" />
              {/if}
              <col style="width: {columnWidths.finalPrice}px;" />
              <col style="width: {columnWidths.createdAt}px;" />
              <col style="width: {ACTIONS_COL_WIDTH}px; min-width: {ACTIONS_COL_WIDTH}px;" />
            </colgroup>
            <thead>
              <tr class="table-header">
                <th class="table-cell resizable-th" style="width: {columnWidths.id}px;">
                  <div class="th-content">ID</div>
                  <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'id')} on:touchstart={(e) => onResizeStart(e, 'id')}></div>
                </th>
                <th class="table-cell resizable-th" style="width: {columnWidths.title}px;">
                  <div class="th-content">{$_('proposals.fields.title')}</div>
                  <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'title')} on:touchstart={(e) => onResizeStart(e, 'title')}></div>
                </th>
                {#if !$isCustomer}
                  <th class="table-cell resizable-th" style="width: {columnWidths.customer}px;">
                    <div class="th-content">{$_('proposals.fields.customer')}</div>
                    <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'customer')} on:touchstart={(e) => onResizeStart(e, 'customer')}></div>
                  </th>
                {/if}
                <th class="table-cell resizable-th" style="width: {columnWidths.status}px;">
                  <div class="th-content">{$_('proposals.fields.status')}</div>
                  <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'status')} on:touchstart={(e) => onResizeStart(e, 'status')}></div>
                </th>
                {#if !$isCustomer}
                  <th class="table-cell resizable-th text-right" style="width: {columnWidths.basePrice}px;">
                    <div class="th-content justify-end">{$_('proposals.fields.basePrice')}</div>
                    <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'basePrice')} on:touchstart={(e) => onResizeStart(e, 'basePrice')}></div>
                  </th>
                  <th class="table-cell resizable-th text-right" style="width: {columnWidths.proposedPrice}px;">
                    <div class="th-content justify-end">{$_('proposals.fields.proposedPrice')}</div>
                    <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'proposedPrice')} on:touchstart={(e) => onResizeStart(e, 'proposedPrice')}></div>
                  </th>
                  <th class="table-cell resizable-th text-right" style="width: {columnWidths.margin}px;">
                    <div class="th-content justify-end">{$_('proposals.fields.margin')}</div>
                    <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'margin')} on:touchstart={(e) => onResizeStart(e, 'margin')}></div>
                  </th>
                {/if}
                <th class="table-cell resizable-th text-right" style="width: {columnWidths.finalPrice}px;">
                  <div class="th-content justify-end">{$_('proposals.fields.finalPrice')}</div>
                  <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'finalPrice')} on:touchstart={(e) => onResizeStart(e, 'finalPrice')}></div>
                </th>
                <th class="table-cell resizable-th" style="width: {columnWidths.createdAt}px;">
                  <div class="th-content">{$_('proposals.fields.createdAt')}</div>
                  <div class="resize-handle" on:mousedown={(e) => onResizeStart(e, 'createdAt')} on:touchstart={(e) => onResizeStart(e, 'createdAt')}></div>
                </th>
                <th class="table-cell sticky-col-header">{$_('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {#each proposals as proposal}
                {@const margin = calculateMargin(proposal)}
                <tr class="table-row">
                  <td class="table-cell font-bold text-heading truncate" style="max-width: {columnWidths.id}px;">
                    {proposal.proposal_number || `#${proposal.id}`}
                    <!-- Every revision of one offer carries the same number, so
                         without this two rows would be indistinguishable. -->
                    {#if proposal.revision > 1}
                      <span class="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded">
                        {$_('proposals.revisions.badge', { values: { revision: proposal.revision } })}
                      </span>
                    {/if}
                  </td>
                  <td class="table-cell truncate" style="max-width: {columnWidths.title}px;">
                    <div class="font-bold text-heading truncate">{proposal.title}</div>
                    {#if proposal.description}
                      <div class="text-xs truncate" style="color: var(--color-text-muted);">{proposal.description}</div>
                    {/if}
                  </td>
                  {#if !$isCustomer}
                    <td class="table-cell truncate" style="max-width: {columnWidths.customer}px; color: var(--color-text-body);">{proposal.customer_name || '-'}</td>
                  {/if}
                  <td class="table-cell" style="max-width: {columnWidths.status}px;">
                    <span class="px-2 py-1 text-xs font-bold border {statusColors[proposal.status] || 'bg-gray-100'}">
                      {$_(`proposals.status.${proposal.status}`)}
                    </span>
                    <!-- What is actually known about the document behind this
                         status: the final one once it exists, otherwise the
                         customer's. A row never implies a verification that
                         was not performed. -->
                    {#if proposalSignatureState(proposal, 'final') || proposalSignatureState(proposal, 'customer')}
                      <span class="block mt-1">
                        <SignatureStateBadge
                          state={proposalSignatureState(proposal, 'final') || proposalSignatureState(proposal, 'customer')}
                          compact={true}
                        />
                      </span>
                    {/if}
                  </td>
                  {#if !$isCustomer}
                    <td class="table-cell text-right truncate" style="max-width: {columnWidths.basePrice}px; color: var(--color-text-muted);">{formatCurrency(proposal.base_price)}</td>
                    <td class="table-cell text-right font-bold text-heading truncate" style="max-width: {columnWidths.proposedPrice}px;">{formatCurrency(proposal.proposed_price)}</td>
                    <td class="table-cell text-right" style="max-width: {columnWidths.margin}px;">
                      <!-- A manually priced line counts at its own price in the
                           base price, so the margin of this proposal would come
                           out as exactly 0 — not because it is zero, but because
                           the cost of that line is not known (it needs hours x
                           hourly cost, which no timesheet feeds here yet).
                           Printing 0% would be quoted as a measurement. -->
                      {#if proposal.has_manual_price}
                        <span
                          class="text-gray-500"
                          title={$_('proposals.fields.marginUnknownHint')}
                        >{$_('proposals.fields.marginUnknown')}</span>
                      {:else if margin.amount !== 0}
                        <div class="flex flex-col items-end">
                          <span class="{margin.amount >= 0 ? 'text-green-400' : 'text-red-400'} font-bold">
                            {margin.amount >= 0 ? '+' : ''}{formatCurrency(margin.amount)}
                          </span>
                          <span class="text-xs {margin.percent >= 0 ? 'text-green-400' : 'text-red-400'}">
                            ({margin.percent >= 0 ? '+' : ''}{margin.percent.toFixed(1)}%)
                          </span>
                        </div>
                      {:else}
                        <span class="text-gray-500">-</span>
                      {/if}
                    </td>
                  {/if}
                  <td class="table-cell text-right truncate" style="max-width: {columnWidths.finalPrice}px;">
                    <span class="font-bold text-primary-500">{formatCurrency(proposal.final_price)}</span>
                    {#if proposal.split_payment}
                      <span
                        class="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        title={$_('proposals.fields.splitPaymentHint')}
                      >{$_('proposals.fields.splitPaymentBadge')}</span>
                    {/if}
                  </td>
                  <td class="table-cell truncate" style="max-width: {columnWidths.createdAt}px; color: var(--color-text-body);">{formatDate(proposal.created_at)}</td>
                  <!-- Actions (sticky right) -->
                  <td class="table-cell sticky-col">
                    <div class="flex items-center space-x-1 flex-nowrap">
                      {#if $isCustomer}
                        <button
                          class="action-btn text-gray-400 hover:text-primary-500"
                          title={$_('proposals.actions.downloadPdf')}
                          on:click={() => downloadPdf(proposal)}
                        >
                          <Download size={15} />
                        </button>
                        <button
                          class="action-btn text-gray-400 hover:text-primary-500"
                          title={$_('proposals.actions.viewHistory')}
                          on:click={() => openHistoryModal(proposal)}
                        >
                          <History size={15} />
                        </button>
                        {#if proposal.status === 'sent' || proposal.status === 'seen' || proposal.status === 'customer_approved'}
                          <button
                            class="action-btn text-cyan-400 hover:text-cyan-300"
                            title={proposalSignatureState(proposal, 'customer') ? $_('proposals.actions.reuploadSignedPdf') : $_('proposals.actions.uploadSignedPdf')}
                            on:click={() => openUploadModal(proposal, 'customer')}
                            disabled={actionLoading}
                          >
                            <Upload size={15} />
                          </button>
                        {/if}
                        {#if proposalSignatureState(proposal, 'customer') || proposalSignatureState(proposal, 'final')}
                          <button
                            class="action-btn text-purple-400 hover:text-purple-300"
                            title={$_('proposals.actions.signatureInfo')}
                            on:click={() => openSignatureModal(proposal)}
                          >
                            <FileSignature size={15} />
                          </button>
                        {/if}
                        {#if proposal.status === 'sent' || proposal.status === 'seen'}
                          <button
                            class="action-btn text-emerald-400 hover:text-emerald-300"
                            title={$_('proposals.actions.approve')}
                            on:click={() => handleCustomerApprove(proposal)}
                            disabled={actionLoading}
                          >
                            <ThumbsUp size={15} />
                          </button>
                        {/if}
                      {:else}
                        {#if proposal.status === 'draft' && $canCreateProposal}
                          <button
                            class="action-btn text-gray-400 hover:text-primary-500"
                            title={$_('common.edit')}
                            on:click={() => openEditModal(proposal)}
                          >
                            <Edit size={15} />
                          </button>
                        {/if}
                        <button
                          class="action-btn text-gray-400 hover:text-primary-500"
                          title={$_('proposals.actions.downloadPdf')}
                          on:click={() => downloadPdf(proposal)}
                        >
                          <Download size={15} />
                        </button>
                        <button
                          class="action-btn text-gray-400 hover:text-primary-500"
                          title={$_('proposals.actions.viewHistory')}
                          on:click={() => openHistoryModal(proposal)}
                        >
                          <History size={15} />
                        </button>
                        {#if proposal.status === 'draft' && $canSendProposal}
                          <button
                            class="action-btn text-blue-400 hover:text-blue-300"
                            title={$_('proposals.actions.sendEmail')}
                            on:click={() => handleSend(proposal)}
                            disabled={actionLoading}
                          >
                            <Send size={15} />
                          </button>
                        {/if}
                        <!-- Revising withdraws the document already sent, so it is
                             gated on the capability to raise a proposal at all:
                             the status a row happens to be in is not
                             authorization. -->
                        {#if $canReviseProposal && (proposal.status === 'sent' || proposal.status === 'seen')}
                          <button
                            class="action-btn text-amber-400 hover:text-amber-300"
                            title={$_('proposals.actions.revise')}
                            on:click={() => openReviseModal(proposal)}
                            disabled={actionLoading}
                          >
                            <RefreshCw size={15} />
                          </button>
                        {/if}
                        {#if proposal.revision > 1 || proposal.supersedes_id != null || proposal.status === 'superseded'}
                          <button
                            class="action-btn text-purple-400 hover:text-purple-300"
                            title={$_('proposals.revisions.title')}
                            on:click={() => openRevisionsModal(proposal)}
                          >
                            <GitBranch size={15} />
                          </button>
                        {/if}
                        {#if proposal.status === 'customer_approved' && $canConfirmProposal}
                          <button
                            class="action-btn text-emerald-400 hover:text-emerald-300"
                            title={$_('proposals.actions.uploadFinalSignedPdf')}
                            on:click={() => openUploadModal(proposal, 'final')}
                            disabled={actionLoading}
                          >
                            <Upload size={15} />
                          </button>
                        {/if}
                        <!-- Counter-sign with the company qualified certificate.
                             Only once the customer has signed and only while no
                             final document exists: the alternative to this button
                             is downloading, signing on the desktop and uploading
                             the result back. -->
                        {#if $canSignDocuments && proposal.status === 'customer_approved' && proposal.customerSignatureState && !proposal.finalSignatureState}
                          <button
                            class="action-btn text-cyan-400 hover:text-cyan-300"
                            title={$_('signatures.countersign')}
                            on:click={() => openCountersign(proposal)}
                            disabled={actionLoading || countersigning}
                          >
                            <PenTool size={15} />
                          </button>
                        {/if}
                        {#if proposal.status === 'customer_approved' && $canConfirmProposal}
                          <button
                            class="action-btn text-emerald-400 hover:text-emerald-300"
                            title={$_('proposals.actions.confirm')}
                            on:click={() => openConfirm(proposal)}
                            disabled={actionLoading}
                          >
                            <Check size={15} />
                          </button>
                        {/if}
                        {#if ['customer_approved', 'confirmed'].includes(proposal.status)}
                          <button
                            class="action-btn text-purple-400 hover:text-purple-300"
                            title={$_('proposals.actions.signatureInfo')}
                            on:click={() => openSignatureModal(proposal)}
                          >
                            <FileSignature size={15} />
                          </button>
                        {/if}
                        {#if $isAdmin && MANUALLY_CONFIRMABLE_STATUSES.includes(proposal.status)}
                          <button
                            class="action-btn text-amber-400 hover:text-amber-300"
                            title={$_('proposals.actions.manualConfirm')}
                            on:click={() => openManualConfirmModal(proposal)}
                            disabled={actionLoading}
                          >
                            <FileCheck size={15} />
                          </button>
                        {/if}
                        {#if CANCELLABLE_STATUSES.includes(proposal.status) && ($canCancelProposal || isOwnDraftRevision(proposal))}
                          <button
                            class="action-btn text-red-400 hover:text-red-300"
                            title={isOwnDraftRevision(proposal) && !$canCancelProposal
                              ? $_('proposals.actions.abandonRevision')
                              : $_('proposals.actions.cancel')}
                            on:click={() => openCancelModal(proposal)}
                            disabled={actionLoading}
                          >
                            <Ban size={15} />
                          </button>
                        {/if}
                        {#if $isAdmin && isDeletable(proposal)}
                          <button
                            class="action-btn text-red-400 hover:text-red-300"
                            title={$_('proposals.actions.delete')}
                            on:click={() => openDeleteModal(proposal)}
                            disabled={actionLoading}
                          >
                            <Trash2 size={15} />
                          </button>
                        {/if}
                      {/if}
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      {#if pagination.pages > 1}
        <div class="flex items-center justify-between px-4">
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {$_('common.page')} {pagination.page} {$_('common.of')} {pagination.pages}
          </span>
          <div class="flex space-x-2">
            <button
              class="btn btn-secondary"
              disabled={pagination.page <= 1}
              on:click={() => { pagination.page--; loadProposals(); }}
            >
              {$_('common.previous')}
            </button>
            <button
              class="btn btn-secondary"
              disabled={pagination.page >= pagination.pages}
              on:click={() => { pagination.page++; loadProposals(); }}
            >
              {$_('common.next')}
            </button>
          </div>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Create/Edit Modal -->
  {#if showCreateModal}
    <ProposalModal
      proposal={editingProposal}
      {customers}
      loading={actionLoading}
      on:save={handleCreateOrUpdate}
      on:close={() => { showCreateModal = false; editingProposal = null; }}
    />
  {/if}

  <!-- Cancel Modal -->
  {#if showCancelModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={() => showCancelModal = false}></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 scale-in">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{$_('proposals.modal.cancelTitle')}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('proposals.modal.cancelSubtitle')}</p>

        <div class="mb-4">
          <label class="label">{$_('proposals.modal.cancelNotes')}</label>
          <textarea
            class="input w-full h-24"
            bind:value={cancelNotes}
            placeholder={$_('proposals.modal.cancelNotesPlaceholder')}
          ></textarea>
        </div>

        <div class="flex justify-end space-x-3">
          <button class="btn btn-secondary" on:click={() => showCancelModal = false}>
            {$_('common.cancel')}
          </button>
          <button
            class="btn btn-danger"
            on:click={handleCancel}
            disabled={!cancelNotes.trim() || actionLoading}
          >
            {actionLoading ? $_('common.loading') : $_('proposals.actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Revise Modal -->
  {#if showReviseModal && selectedProposal}
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={() => showReviseModal = false}></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 scale-in">
        <div class="flex items-center space-x-3 mb-4">
          <div class="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
            <RefreshCw class="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{$_('proposals.modal.reviseTitle')}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{selectedProposal.proposal_number || selectedProposal.title}</p>
          </div>
        </div>

        <!-- The dead end, stated before the click rather than discovered after
             it: the customer keeps the PDF that was e-mailed to them. -->
        <div class="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
          <p class="text-sm text-amber-800 dark:text-amber-200">{$_('proposals.modal.reviseWarning')}</p>
          <p class="text-sm text-amber-800 dark:text-amber-200 mt-2">{$_('proposals.modal.reviseSignatureWarning')}</p>
        </div>

        <div class="mb-4">
          <label class="label">{$_('proposals.modal.reviseReason')}</label>
          <textarea
            class="input w-full h-24"
            bind:value={reviseReason}
            placeholder={$_('proposals.modal.reviseReasonPlaceholder')}
          ></textarea>
        </div>

        <div class="flex justify-end space-x-3">
          <button class="btn btn-secondary" on:click={() => showReviseModal = false}>
            {$_('common.cancel')}
          </button>
          <button
            class="btn btn-primary"
            on:click={handleRevise}
            disabled={!reviseReason.trim() || actionLoading}
          >
            {actionLoading ? $_('common.loading') : $_('proposals.actions.revise')}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Revision chain -->
  {#if showRevisionsModal && selectedProposal}
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={() => showRevisionsModal = false}></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6 scale-in">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">{$_('proposals.revisions.title')}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('proposals.revisions.subtitle')}</p>

        {#if revisionsLoading}
          <div class="flex justify-center py-6">
            <Loader class="h-6 w-6 animate-spin text-primary-500" />
          </div>
        {:else if revisions.length === 0}
          <p class="text-gray-500 dark:text-gray-400 text-center py-4">{$_('proposals.revisions.none')}</p>
        {:else}
          <div class="space-y-3 max-h-72 overflow-y-auto">
            {#each revisions as item}
              <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg {item.id === selectedProposal.id ? 'ring-1 ring-primary-500' : ''}">
                <div class="flex items-center justify-between">
                  <span class="font-bold text-heading">
                    {item.proposal_number} {$_('proposals.revisions.badge', { values: { revision: item.revision } })}
                  </span>
                  <span class="px-2 py-1 rounded text-xs font-medium {statusColors[item.status] || 'bg-gray-100 dark:bg-gray-700'}">
                    {$_(`proposals.status.${item.status}`)}
                  </span>
                </div>
                <!-- What this revision says, not only when it moved. The panel
                     is opened to find out what the first version offered, or
                     what changed in the current one; dates and a digest cannot
                     answer either. -->
                <p class="text-sm font-medium text-heading mt-1 line-clamp-2">{item.title}</p>
                {#if item.description}
                  <p class="text-xs line-clamp-2" style="color: var(--color-text-muted);">{item.description}</p>
                {/if}
                {#if item.revision_reason}
                  <p class="text-xs mt-1" style="color: var(--color-text-muted);">
                    <span class="font-medium">{$_('proposals.revisions.reason')}:</span> {item.revision_reason}
                  </p>
                {/if}
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {#if item.issued_at}
                    {$_('proposals.revisions.issuedAt')}: {formatDate(item.issued_at)}
                  {:else}
                    {$_('proposals.revisions.notIssued')}
                  {/if}
                  {#if item.superseded_at}
                    &middot; {$_('proposals.revisions.supersededAt')}: {formatDate(item.superseded_at)}
                  {/if}
                </p>
                <!-- The digest of the document that revision transmitted: the
                     evidence a signature is checked against. -->
                {#if item.original_pdf_sha256}
                  <p class="text-[11px] font-mono text-gray-400 dark:text-gray-500 mt-1 truncate">
                    {$_('proposals.revisions.digest')}: {item.original_pdf_sha256.slice(0, 16)}…
                  </p>
                {/if}
                <!-- A revision listed and unopenable is what a customer dispute
                     runs into: these are the two things there is to do with one. -->
                <div class="flex items-center gap-3 mt-2">
                  <button
                    class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    on:click={() => openRevisionInList(item)}
                  >
                    {$_('proposals.revisions.openInList')}
                  </button>
                  <button
                    class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    on:click={() => downloadPdf(item)}
                  >
                    {$_('proposals.revisions.downloadDocument')}
                  </button>
                </div>
              </div>
            {/each}
          </div>
        {/if}

        <div class="flex justify-end mt-4">
          <button class="btn btn-secondary" on:click={() => showRevisionsModal = false}>
            {$_('common.close')}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Delete Modal -->
  {#if showDeleteModal && selectedProposal}
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={() => showDeleteModal = false}></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 scale-in">
        <div class="flex items-center space-x-3 mb-4">
          <div class="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-xl flex items-center justify-center">
            <Trash2 class="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{$_('proposals.modal.deleteTitle')}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{selectedProposal.proposal_number || selectedProposal.title}</p>
          </div>
        </div>

        <div class="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg mb-4">
          <p class="text-sm text-red-700 dark:text-red-300">{$_('proposals.modal.deleteWarning')}</p>
        </div>

        <div class="flex justify-end space-x-3">
          <button class="btn btn-secondary" on:click={() => showDeleteModal = false}>
            {$_('common.cancel')}
          </button>
          <button
            class="btn btn-danger"
            on:click={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? $_('common.loading') : $_('proposals.actions.delete')}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- History Modal -->
  {#if showHistoryModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={() => showHistoryModal = false}></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6 scale-in">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{$_('proposals.history.title')}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('proposals.history.subtitle')}</p>

        {#if history.length === 0}
          <p class="text-gray-500 dark:text-gray-400 text-center py-4">{$_('proposals.history.noHistory')}</p>
        {:else}
          <div class="space-y-3 max-h-64 overflow-y-auto">
            {#each history as item}
              <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div class="flex items-center justify-between">
                  <span class="px-2 py-1 rounded text-xs font-medium {statusColors[item.new_status] || 'bg-gray-100 dark:bg-gray-700'}">
                    {$_(`proposals.status.${item.new_status}`)}
                  </span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(item.created_at)}
                  </span>
                </div>
                {#if item.changed_by}
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {$_('proposals.history.changedBy')}: {item.changed_by_email || item.changed_by}
                  </p>
                {/if}
                {#if item.notes}
                  <p class="text-sm text-gray-600 dark:text-gray-300 mt-2">{item.notes}</p>
                {/if}
              </div>
            {/each}
          </div>
        {/if}

        <div class="flex justify-end mt-4">
          <button class="btn btn-secondary" on:click={() => showHistoryModal = false}>
            {$_('common.close')}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Signature Info Modal -->
  {#if showSignatureModal && selectedProposal}
    <SignatureInfoModal
      proposalId={selectedProposal.id}
      proposalNumber={selectedProposal.proposal_number || selectedProposal.id}
      isCustomerUser={$isCustomer}
      on:close={() => { showSignatureModal = false; selectedProposal = null; }}
    />
  {/if}

  <!-- Upload Signed PDF Modal -->
  {#if showUploadModal && selectedProposal}
    <UploadSignedPdfModal
      proposalId={selectedProposal.id}
      proposalNumber={selectedProposal.proposal_number || selectedProposal.id}
      type={uploadType}
      isCustomerUser={$isCustomer}
      on:uploaded={handleUploadSuccess}
      on:close={() => { showUploadModal = false; selectedProposal = null; }}
    />
  {/if}

  <!-- Qualified counter-signature confirmation (admin only) -->
  {#if showCountersignModal && selectedProposal}
    <ConfirmModal
      title={$_('signatures.countersignConfirmTitle')}
      message={$_('signatures.countersignConfirmMessage')}
      confirmText={countersigning ? $_('signatures.countersigning') : $_('signatures.countersign')}
      confirmClass="btn-primary"
      loading={countersigning}
      on:confirm={() => handleCountersign()}
      on:cancel={() => { showCountersignModal = false; selectedProposal = null; }}
    />
  {/if}

  <!-- Attestation of a document the system could not verify. Shown before the
       counter-signature or the confirmation, never after the fact. -->
  {#if showAttestationModal && selectedProposal}
    <SignatureAttestationModal
      action={attestationAction}
      proposalNumber={selectedProposal.proposal_number || selectedProposal.id}
      info={attestationInfo}
      signatureState={attestationState}
      serverMessage={attestationServerMessage}
      loading={attestationLoading}
      error={attestationError}
      on:attest={handleAttest}
      on:close={closeAttestation}
    />
  {/if}

  <!-- Manual Confirm Modal (Admin only) -->
  {#if showManualConfirmModal && selectedProposal}
    <ManualConfirmModal
      proposalId={selectedProposal.id}
      proposalNumber={selectedProposal.proposal_number || selectedProposal.id}
      on:confirmed={handleManualConfirmSuccess}
      on:close={() => { showManualConfirmModal = false; selectedProposal = null; }}
    />
  {/if}
</Layout>

<style>
  /* Sticky actions column (right) */
  .sticky-col-header,
  .sticky-col {
    position: sticky;
    right: 0;
    z-index: 10;
    background-color: var(--color-surface-dark);
    border-left: 2px solid var(--color-border-default);
  }

  .sticky-col-header {
    background-color: var(--color-surface-light);
    z-index: 11;
  }

  /* `.table-cell` carries px-6, which is right for text and wasteful for a strip
     of icon buttons: 48px of the actions column would go to padding. */
  .sticky-col,
  .sticky-col-header {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  /* Resize handles on column headers */
  .resizable-th {
    position: relative;
    user-select: none;
    overflow: hidden;
  }

  .th-content {
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    background: transparent;
    transition: background 0.1s;
    z-index: 1;
  }

  .resize-handle:hover,
  .resize-handle:active {
    background: var(--color-primary, #00f0ff);
    opacity: 0.6;
  }

  /* Action buttons inside table */
  .action-btn {
    padding: 4px;
    transition: color 0.1s;
    flex-shrink: 0;
    cursor: pointer;
    background: none;
    border: none;
  }

  .action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Table wrapper */
  .proposals-table-wrapper {
    position: relative;
  }

  .proposals-scroll-area {
    overflow-x: auto;
  }

  .proposals-table {
    border-collapse: collapse;
  }

  /* Prevent text overflow in cells */
  .proposals-table td.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
